/**
 * Shared matchers for the backend's listing publish gate and the paperwork
 * errors a listing save can return.
 *
 * PUT /api/houses/{id}/status?status=active returns 409 with a structured
 * `detail` ({code, message, ...}) when a publish rule refuses the listing.
 * APIAgent puts the human-readable detail.message in err.message and the raw
 * detail object in err.detail, so the code has to be read from err.detail;
 * the message check is a fallback for older error shapes.
 *
 * Paperwork (docs/architecture/paperwork-categories.md §8.2):
 *   - 422 paperwork_blocked            — create/update with a hard block
 *   - 409 paperwork_category_locked    — category/basis change while locked,
 *                                        or (`field: "state"`) a state change;
 *                                        every lock carries `under_contract`
 *   - 409 paperwork_category_required  — publish, or active → pending
 *                                        (Negotiate), with undetermined paperwork
 *   - 409 listing_under_contract       — withdraw / back to draft while under
 *                                        contract (`reason` says why)
 *   - 409 paperwork_category_unsupported — publish, paperwork not ready
 *   - 409 condition_report_required    — publish, disclosure incomplete
 *
 * Property Features (mlsFieldSchema.js; backend app/services/property_features.py):
 *   - 409 property_features_incomplete — publish with a required Property
 *                                        Features answer blank (`missing`, keys)
 *                                        or more than two exterior materials
 *                                        (`over_limit`); `labels` names both
 *
 * Ownership verification (docs/plans/ownership-verification/README.md):
 *   - 409 ownership_verification_required — publish, ownership not verified
 *   - 409 address_change_requires_withdrawal — address edit on a listing
 *                                        that isn't draft or withdrawn
 */

import { stateCopy } from './paperworkCopy';

/**
 * @param {Error & {status?: number, statusCode?: number, detail?: any}} err
 * @returns {boolean} true when the publish was refused for a missing signature.
 */
export function isListingAgreementRequired(err) {
  return publishRefusalCode(err) === 'listing_agreement_required';
}

export const PAPERWORK_BLOCKED = 'paperwork_blocked';
export const PAPERWORK_CATEGORY_LOCKED = 'paperwork_category_locked';
export const PAPERWORK_CATEGORY_REQUIRED = 'paperwork_category_required';
export const PAPERWORK_CATEGORY_UNSUPPORTED = 'paperwork_category_unsupported';
export const CONDITION_REPORT_REQUIRED = 'condition_report_required';
export const PROPERTY_FEATURES_INCOMPLETE = 'property_features_incomplete';
/**
 * Publish refused for a missing required upload (the interim WI § 709.02(2)
 * condominium addendum). CONTRACT ASSUMPTION: the design doc doesn't define
 * this refusal; the frontend expects 409 {code: 'required_upload_missing',
 * upload_code, message}.
 */
export const REQUIRED_UPLOAD_MISSING = 'required_upload_missing';

// Every 409 the publish gate can return (backend/app/routes/houses.py
// _enforce_publish_gates). A refusal means "finish the listing first", so the
// UI should say what's missing rather than "try again".
const PUBLISH_REFUSAL_CODES = [
  'listing_agreement_required',
  'listing_price_required',
  CONDITION_REPORT_REQUIRED,
  PAPERWORK_CATEGORY_REQUIRED,
  PAPERWORK_CATEGORY_UNSUPPORTED,
  REQUIRED_UPLOAD_MISSING,
  PROPERTY_FEATURES_INCOMPLETE,
  // Ownership not verified yet (detail also carries `verification_status`).
  'ownership_verification_required',
];

const statusOf = (err) => err?.status ?? err?.statusCode;

// ── Condition report status (GET /api/houses/{id}/disclosure) ───────────

/** The report is signed: it counts for the publish gate. */
export const DISCLOSURE_STATUS_COMPLETED = 'completed';
/** Out for the seller's DocuSign signature: answers locked, NOT complete. */
export const DISCLOSURE_STATUS_SIGNING = 'signing';

/** The status string of a disclosure payload (or a bare status string). */
const disclosureStatusOf = (status) =>
  typeof status === 'string' ? status : status?.status ?? null;

/**
 * True when the condition report counts for publishing. Only a signed
 * (`completed`) report does; one waiting in DocuSign (`signing`) does not.
 *
 * @param {object|string|null} status - the disclosure payload or its status
 * @returns {boolean}
 */
export function isDisclosureComplete(status) {
  return disclosureStatusOf(status) === DISCLOSURE_STATUS_COMPLETED;
}

/**
 * True while the report is out for the seller's DocuSign signature.
 *
 * @param {object|string|null} status - the disclosure payload or its status
 * @returns {boolean}
 */
export function isDisclosureSigning(status) {
  return disclosureStatusOf(status) === DISCLOSURE_STATUS_SIGNING;
}

/**
 * True when a listing save was refused because the address changed on a
 * listing that isn't draft or withdrawn (PUT /houses/{id} 409
 * `address_change_requires_withdrawal`). Code from err.detail, message fallback.
 *
 * @param {Error & {status?: number, statusCode?: number, detail?: any}} err
 * @returns {boolean}
 */
export function isAddressChangeRequiresWithdrawal(err) {
  if (!err || (err.status ?? err.statusCode) !== 409) return false;
  if (err.detail?.code === 'address_change_requires_withdrawal') return true;
  const msg = typeof err.message === 'string' ? err.message : '';
  return msg.includes('address_change_requires_withdrawal');
}

/**
 * PUT /api/houses/{id} 409 `condition_report_signing`: the address, city,
 * state or ZIP changed while the condition report is out for the seller's
 * DocuSign signature (the report names the address being signed).
 */
export const CONDITION_REPORT_SIGNING = 'condition_report_signing';

/** Shown when that refusal carries no message, and beside the locked fields. */
export const ADDRESS_SIGNING_LOCKED_MESSAGE =
  'Finish or decline the DocuSign signature before changing the address.';

/**
 * @param {Error & {status?: number, statusCode?: number, detail?: any}} err
 * @returns {boolean} true for a 409 `condition_report_signing`.
 */
export function isConditionReportSigning(err) {
  if (!err || (err.status ?? err.statusCode) !== 409) return false;
  if (err.detail?.code === CONDITION_REPORT_SIGNING) return true;
  const msg = typeof err.message === 'string' ? err.message : '';
  return msg.includes(CONDITION_REPORT_SIGNING);
}

/**
 * True when publish was refused because property ownership isn't verified.
 *
 * @param {Error & {status?: number, statusCode?: number, detail?: any}} err
 * @returns {boolean}
 */
export function isOwnershipVerificationRequired(err) {
  return publishRefusalCode(err) === 'ownership_verification_required';
}

/**
 * Which publish rule refused a status change, or null for any other failure.
 *
 * @param {Error & {status?: number, statusCode?: number, detail?: any}} err
 * @returns {string|null} one of PUBLISH_REFUSAL_CODES, or null.
 */
export function publishRefusalCode(err) {
  if (!err || statusOf(err) !== 409) return null;
  if (PUBLISH_REFUSAL_CODES.includes(err.detail?.code)) return err.detail.code;
  const msg = typeof err.message === 'string' ? err.message.toLowerCase() : '';
  return PUBLISH_REFUSAL_CODES.find((code) => msg.includes(code)) || null;
}

const PAPERWORK_ERROR_STATUS = {
  [PAPERWORK_BLOCKED]: 422,
  [PAPERWORK_CATEGORY_LOCKED]: 409,
  [PAPERWORK_CATEGORY_REQUIRED]: 409,
  [PAPERWORK_CATEGORY_UNSUPPORTED]: 409,
};

/**
 * The paperwork error code of a failed save or publish, or null.
 *
 * @param {Error & {status?: number, detail?: any}} err
 * @returns {string|null} one of the PAPERWORK_* codes
 */
export function paperworkErrorCode(err) {
  const code = err?.detail?.code;
  if (!code || !(code in PAPERWORK_ERROR_STATUS)) return null;
  return statusOf(err) === PAPERWORK_ERROR_STATUS[code] ? code : null;
}

/**
 * The server's own message for a structured refusal (`detail.message`), or
 * null. These messages are the reviewed copy for the listing's state and
 * form, so they are shown as-is.
 */
export function refusalMessage(err) {
  const message = err?.detail?.message;
  return typeof message === 'string' && message.trim() ? message.trim() : null;
}

// ── Listing locks (409) ─────────────────────────────────────────────────

export const LISTING_UNDER_CONTRACT = 'listing_under_contract';
/** 409 when Mark Sold is refused because an offer or counteroffer is still open (QA S13). */
export const LISTING_HAS_OPEN_OFFERS = 'listing_has_open_offers';
export const LISTING_HAS_OPEN_OFFERS_FALLBACK =
  "This listing still has open offers or counteroffers, so it can't be marked sold here. Decline them in Negotiations first, or wait for them to expire.";

/** Generic fallbacks when the server sends no message and the state has no reviewed copy. */
export const LISTING_UNDER_CONTRACT_FALLBACK =
  "This listing is under contract, so it can't be withdrawn or moved back to draft here.";
/** The same refusal for relisting or marking sold (QA S13). */
export const LISTING_UNDER_CONTRACT_STATUS_FALLBACK =
  "This listing is under contract, so it can't be relisted or marked sold here. It will show as sold once you and the buyer both finish the closing checklist.";
export const PAPERWORK_LOCKED_FALLBACK =
  "This listing's paperwork can't be changed while it is on the market or under contract.";

// Seller moves that take a listing off the market.
const OFF_MARKET_TARGETS = new Set(['draft', 'withdrawn']);

const detailOf = (err) => (err?.detail && typeof err.detail === 'object' ? err.detail : {});

/**
 * True for 409 `listing_under_contract`: the seller tried to move a listing
 * that is under contract anywhere but `pending` (withdraw, back to draft,
 * relist, or mark sold).
 */
export function isListingUnderContract(err) {
  return statusOf(err) === 409 && detailOf(err).code === LISTING_UNDER_CONTRACT;
}

/** True for 409 `listing_has_open_offers`: Mark Sold while an offer could still be accepted. */
export function hasOpenOffersRefusal(err) {
  return statusOf(err) === 409 && detailOf(err).code === LISTING_HAS_OPEN_OFFERS;
}

/**
 * The message for a 409 `listing_under_contract`: the server's, else legal's
 * copy for the move (`<STATE>.paperwork.listing_under_contract` for taking it
 * off the market, `..._status` for relisting or marking sold), else a generic line.
 *
 * @param {Error} err
 * @param {string} [stateCode] - The listing's state, for the fallback copy.
 * @param {string} [targetStatus] - The refused status; off-market when omitted.
 * @returns {string}
 */
export function listingUnderContractMessage(err, stateCode, targetStatus) {
  const offMarket = targetStatus == null || OFF_MARKET_TARGETS.has(targetStatus);
  return (
    refusalMessage(err) ||
    (offMarket
      ? stateCopy(stateCode, 'paperwork.listing_under_contract') || LISTING_UNDER_CONTRACT_FALLBACK
      : stateCopy(stateCode, 'paperwork.listing_under_contract_status') ||
        LISTING_UNDER_CONTRACT_STATUS_FALLBACK)
  );
}

/** True for a 409 `paperwork_category_locked` about the state (`field: "state"`). */
export function isStateLock(err) {
  return paperworkErrorCode(err) === PAPERWORK_CATEGORY_LOCKED && detailOf(err).field === 'state';
}

/**
 * The message for a 409 `paperwork_category_locked`: the server's, else
 * legal's copy for the lock (state or property type; on the market or under
 * contract, from the detail's `under_contract`), else a generic line.
 *
 * @param {Error} err
 * @param {string} [stateCode] - The listing's state, when the detail has none.
 * @returns {string}
 */
export function paperworkLockMessage(err, stateCode) {
  const server = refusalMessage(err);
  if (server) return server;
  const detail = detailOf(err);
  const state = detail.current_state || stateCode;
  const underContract = detail.under_contract === true;
  let key;
  if (detail.field === 'state') {
    key = underContract ? 'paperwork.locked.state_under_contract' : 'paperwork.locked.state_active';
  } else if (underContract) {
    key = 'paperwork.locked.under_contract';
  }
  // `paperwork.locked.active` needs the report's title, which the detail
  // doesn't carry, so it falls through to the generic line.
  return (key && stateCopy(state, key)) || PAPERWORK_LOCKED_FALLBACK;
}
